import React, { useState, useEffect } from "react";
import { Heart, Star, ListCollapse, ChevronDown, Filter, X } from "lucide-react";
import "./ProductSection.css";
import { useNavigate } from 'react-router-dom';
import axios from "axios";

const ProductSection = ({ searchTerm, setSearchTerm }) => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("");
  const [priceRange, setPriceRange] = useState(1500);
  const [favorites, setFavorites] = useState(new Set());
  const [categoryOpen, setCategoryOpen] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOption, setSortOption] = useState("price-asc");
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsResponse, categoriesResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BASE_URL}/products/Allproducts`),
          axios.get(`${process.env.REACT_APP_BASE_URL}/categories/`)
        ]);

        setProducts(productsResponse.data.data);
        setCategories([
          { name: "All Products", value: "" },
          ...categoriesResponse.data.data.map(category => ({
            name: category.name,
            value: category.name,
          })),
        ]);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);
  // Check if ProductSection receives updated searchTerm
  useEffect(() => {
    console.log("searchTerm inside ProductSection:", searchTerm);
  }, [searchTerm]);

  const toggleFavorite = (productId) => {
    const newFavorites = new Set(favorites);
    newFavorites.has(productId) ? newFavorites.delete(productId) : newFavorites.add(productId);
    setFavorites(newFavorites);
  };

  const sortProducts = (products) => {
    switch (sortOption) {
      case "price-asc":
        return [...products].sort((a, b) => a.price - b.price);
      case "price-desc":
        return [...products].sort((a, b) => b.price - a.price);
      case "rating":
        return [...products].sort((a, b) => (b.rating || 0) - (a.rating || 0));
      case "newest":
        return [...products].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      default:
        return products;
    }
  };

  const filteredProducts = sortProducts(products.filter(product => {
    const categoryMatch = selectedCategory === "" || (product.category?.toLowerCase() === selectedCategory.toLowerCase());
    const priceMatch = product.price <= priceRange;
    const searchMatch =
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return categoryMatch && priceMatch && searchMatch;
  }));



  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;

    // If it's already a full URL (http/https), return as-is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }

    // Otherwise, construct URL from base (if needed)
    const baseUrl = process.env.REACT_APP_BASE_URL?.split('/api')[0] || '';
    return `${baseUrl}/${imagePath.replace(/^\//, '')}`;
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="loading-spinner"></div>
        <p>Loading products...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-overlay">
        <div className="error-icon">⚠️</div>
        <h3>Error Loading Products</h3>
        <p>{error}</p>
        <button className="retry-button" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }
  return (
    <div className="product-section">
      {/* Mobile Filter Trigger */}
      <button className="mobile-filter-trigger" onClick={() => setMobileFiltersOpen(true)}>
        <Filter size={18} />
        <span>Filters</span>
      </button>

      {/* Mobile Filter Panel */}
      {mobileFiltersOpen && (
        <div className="mobile-filter-container">
          <div className="mobile-filter-backdrop" onClick={() => setMobileFiltersOpen(false)}></div>
          <div className="mobile-filter-drawer">
            <div className="filter-header">
              <h3>Filters</h3>
              <button className="close-button" onClick={() => setMobileFiltersOpen(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="filter-section">
              <div className="filter-section-title" onClick={() => setCategoryOpen(!categoryOpen)}>
                <span>Categories</span>
                <ChevronDown className={`dropdown-icon ${categoryOpen ? 'open' : ''}`} size={20} />
              </div>

              {categoryOpen && (
                <div className="filter-options">
                  {categories.map((category, index) => (
                    <label key={index} className="filter-option">
                      <input
                        type="radio"
                        name="category"
                        value={category.value}
                        checked={selectedCategory === category.value}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                      />
                      <span className="checkmark"></span>
                      <span className="option-label">{category.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-section">
              <div className="filter-section-title">
                <span>Price Range</span>
                <span className="price-range-value">₹0 - ₹{priceRange}</span>
              </div>

              <input
                type="range"
                min="0"
                max="3000"
                value={priceRange}
                onChange={(e) => setPriceRange(parseInt(e.target.value))}
                className="price-slider"
                style={{
                  background: `linear-gradient(to right, #4a6bff 0%, #4a6bff ${(priceRange / 3000) * 100}%, #e0e0e0 ${(priceRange / 3000) * 100}%, #e0e0e0 100%)`
                }}
              />

              <div className="price-limits">
                <span>₹0</span>
                <span>₹3000</span>
              </div>
            </div>

            <div className="filter-actions">
              <button
                className="reset-button"
                onClick={() => {
                  setSelectedCategory("");
                  setPriceRange(1500);
                  setSortOption("price-asc");
                }}
              >
                Reset All
              </button>
              <button
                className="apply-button"
                onClick={() => setMobileFiltersOpen(false)}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="product-layout">
        {/* Desktop Filters */}
        <aside className="sidebar-filters">
          <div className="filter-card">
            <h3 className="filter-title">Filters</h3>

            <div className="filter-group">
              <div className="filter-group-header" onClick={() => setCategoryOpen(!categoryOpen)}>
                <span>Categories</span>
                <ChevronDown className={`dropdown-icon ${categoryOpen ? 'open' : ''}`} size={18} />
              </div>

              {categoryOpen && (
                <div className="filter-options">
                  {categories.map((category, index) => (
                    <label key={index} className="filter-option">
                      <input
                        type="radio"
                        name="category"
                        value={category.value}
                        checked={selectedCategory === category.value}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                      />
                      <span className="checkmark"></span>
                      <span className="option-label">{category.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-group">
              <div className="filter-group-header">
                <span>Price Range</span>
                <span className="price-range-value">₹0 - ₹{priceRange}</span>
              </div>

              <input
                type="range"
                min="0"
                max="3000"
                value={priceRange}
                onChange={(e) => setPriceRange(parseInt(e.target.value))}
                className="price-slider"
                style={{
                  background: `linear-gradient(to right, #4a6bff 0%, #4a6bff ${(priceRange / 3000) * 100}%, #e0e0e0 ${(priceRange / 3000) * 100}%, #e0e0e0 100%)`
                }}
              />

              <div className="price-limits">
                <span>₹0</span>
                <span>₹3000</span>
              </div>
            </div>

            <button
              className="clear-filters"
              onClick={() => {
                setSelectedCategory("");
                setPriceRange(1500);
                setSortOption("price-asc");
              }}
            >
              Clear All Filters
            </button>
          </div>
        </aside>

        {/* Main Product Grid */}
        <main className="product-grid-container">
          <div className="product-grid-header">
            <h2 className="product-count">
              {filteredProducts.length} {filteredProducts.length === 1 ? 'Product' : 'Products'} Found
            </h2>

            <div className="sort-controls">
              <label htmlFor="sort-select" className="sort-label">Sort by:</label>
              <select
                id="sort-select"
                className="sort-select"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
              >
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="rating">Highest Rating</option>
                <option value="newest">Newest Arrivals</option>
              </select>
            </div>
          </div>

          {filteredProducts.length > 0 ? (
            <div className="product-grid">
              {filteredProducts.map((product) => (
                <article key={product._id} className="product-card">
                  <div className="product-image-wrapper">
                    {product.images && product.images.length > 0 ? (
                      <img
                        src={getImageUrl(product.images[0].path)}
                        alt={product.name}
                        className="product-image"
                        loading="lazy"
                      />
                    ) : (
                      <div className="image-placeholder">
                        <span>No Image</span>
                      </div>
                    )}

                    {product.discount > 0 && (
                      <div className="discount-badge">
                        -{product.discount}%
                      </div>
                    )}

                    {/* <button
                      className={`favorite-button ${favorites.has(product._id) ? 'active' : ''}`}
                      onClick={() => toggleFavorite(product._id)}
                      aria-label={favorites.has(product._id) ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <Heart size={18} fill={favorites.has(product._id) ? "currentColor" : "none"} />
                    </button> */}
                  </div>

                  <div className="product-details">
                    <span className="product-category">{product.category || 'Generic'}</span>
                    <h3 className="product-title">{product.name?.length > 10 ? `${product.name.substring(0, 10)}...` : product.name}</h3>

                    <p className="product-descriptions">
                      {product.description?.length > 100
                        ? `${product.description.substring(0, 20)}...`
                        : product.description || 'No description available'}
                    </p>

                    <div className="product-meta">
                      <div className="product-rating">
                        <Star size={16} fill="#FFD700" color="#FFD700" />
                        <span>{product.rating?.toFixed(1) || '4.0'}</span>
                      </div>
                    </div>

                    <div className="product-pricing">
                      <div className="price-container">
                        <span className="current-price">₹{product.price?.toFixed(2) || '0.00'}</span>
                        {product.originalPrice > product.price && (
                          <span className="original-price">₹{product.originalPrice?.toFixed(2)}</span>
                        )}
                      </div>
                    </div>

                    <button
                      className="view-details-button"
                      onClick={() => navigate(`/products/${product._id}`)}
                    /* From Uiverse.io by vinodjangid07 */
                    >
                      {/* <span className="IconContainer">
                        <ListCollapse size={16} className="Picon" />
                      </span> */}
                      <p className="text">View Details</p>
                    </button>

                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">🔍</div>
              <h3>No Products Found</h3>
              <p>Try adjusting your filters to find what you're looking for.</p>
              <button
                className="reset-filters-button"
                onClick={() => {
                  setSelectedCategory("");
                  setPriceRange(1500);
                  setSortOption("price-asc");
                }}
              >
                Reset All Filters
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ProductSection;